/**
 * This is a fancy dropdown component that provides autocomplete functionality,
 * as well as (conditional) asynchronous searching for options.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { pluck } from 'spark-util/objects';
import HookFormField from 'spark-ui/form/hookFormAdapter';
import SingleSelect from 'spark-ui/ui-kitchen/ingredients/veggies/fields/dropdowns/SingleSelect';
import Multiselect from 'spark-ui/ui-kitchen/ingredients/veggies/fields/dropdowns/MultipleSelect';
import Async from 'spark-ui/ui-kitchen/ingredients/veggies/fields/dropdowns/AsyncSelect';
import InputErrorMessage from 'spark-ui/ui-kitchen/ingredients/spices/text/InputErrorMessage';

const Dropdown = ({
    label,
    disabled,
    input,
    options,
    placeholder,
    multiple,
    minSearchLength,
    simpleValue,
    size,
    meta: { touched, error },
    inputRef,
    disabledValue,
    onChange: onChangeFromConnect,
    ...rest
}) => {
    const isAsync = typeof options === 'function';
    const Component = isAsync ? Async : multiple ? Multiselect : SingleSelect;
    return (
        <div>
            <Component
                minSearchLength={minSearchLength}
                placeholder={placeholder}
                label={label}
                name={input.name}
                onBlur={() => input.onBlur()}
                onChange={value => {
                    if (simpleValue && multiple) {
                        value = (value && value.map(pluck('value'))) || [];
                    }
                    input.onChange(value);
                    if (typeof onChangeFromConnect === 'function') {
                        onChangeFromConnect(value);
                    }
                }}
                initialValue={input.value || (multiple ? [] : '')}
                disabled={disabled}
                options={options}
                disabledValue={disabledValue}
                multiple={multiple}
                size={size}
                inputRef={inputRef}
                {...rest}
            />
            <InputErrorMessage>{touched && error}</InputErrorMessage>
        </div>
    );
};

Dropdown.propTypes = {
    options: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            })
        ),
        PropTypes.func
    ]).isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    multiple: PropTypes.bool,
    minSearchLength: PropTypes.number,
    input: PropTypes.object,
    meta: PropTypes.object
};

const SelectField = (props) => {
    const { control, ...rest } = props;
    if (control) {
        return <HookFormField {...rest} control={control} component={Dropdown} />;
    }
    return <Field {...rest} component={Dropdown} />;
};

export default SelectField;
